import { useState, useCallback, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AddCircleOutline as NewMatchIcon,
  ClearAll as ClearAllIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  Close as CloseIcon,
  SportsScore as SportsScoreIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  getNotifications,
  markAllAsRead,
  clearAllNotifications,
  deleteNotification,
  ScheduleNotification,
  ScheduleNotificationType,
} from '@/services/notificationService';
import { useNotifications } from '@/contexts/EventStreamContext';
import { SwipeableNotificationItem } from './SwipeableNotificationItem';
import { enqueue } from '@/utils/offlineMutationQueue';
import { clearPushNotifications } from '@/utils/clearPushNotifications';

const NOTIFICATION_ICONS: Record<ScheduleNotificationType, React.ElementType> = {
  MATCH_DATE_CHANGED: EventIcon,
  MATCH_TIME_CHANGED: ScheduleIcon,
  MATCH_LOCATION_CHANGED: PlaceIcon,
  MATCH_NOW_SCHEDULED: CheckCircleIcon,
  MATCH_NOW_PENDING: WarningIcon,
  CONFLICT_DETECTED: WarningIcon,
  CONFLICT_RESOLVED: CheckCircleIcon,
  NEW_MATCH_ADDED: NewMatchIcon,
  SYNC_COMPLETE: SyncIcon,
  SCORE_POSTED: SportsScoreIcon,
};

const NOTIFICATION_COLORS: Record<ScheduleNotificationType, string> = {
  MATCH_DATE_CHANGED: '#1976d2',
  MATCH_TIME_CHANGED: '#f57c00',
  MATCH_LOCATION_CHANGED: '#7b1fa2',
  MATCH_NOW_SCHEDULED: '#388e3c',
  MATCH_NOW_PENDING: '#d32f2f',
  CONFLICT_DETECTED: '#d32f2f',
  CONFLICT_RESOLVED: '#388e3c',
  NEW_MATCH_ADDED: '#00897b',
  SYNC_COMPLETE: '#1565c0',
  SCORE_POSTED: '#2e7d32',
};

// Helper to get a short description of the change (without team names - those are shown separately)
const getChangeDescription = (notification: ScheduleNotification): string => {
  const details = notification.changeDetails;

  // Combined notification (multiple fields changed at once)
  // New format: { homeTeam, awayTeam, changes: [...] }, old format: [...]
  const changesArray = Array.isArray(details) ? details : details?.changes;
  if (Array.isArray(changesArray)) {
    const fieldMap: Record<string, string> = {};
    for (const d of changesArray) {
      if (d.field && d.newValue) fieldMap[d.field] = d.newValue;
    }
    const parts: string[] = [];
    if (fieldMap.date) parts.push(`on ${fieldMap.date}`);
    if (fieldMap.time) parts.push(`at ${fieldMap.time}`);
    // Venue rendered separately
    if (parts.length > 0) return `Now ${parts.join(' ')}`;
    if (fieldMap.location) return `Moved to ${fieldMap.location}`;
    return 'Multiple fields updated';
  }

  switch (notification.type) {
    case 'MATCH_DATE_CHANGED':
      return details?.newValue ? `Rescheduled to ${details.newValue}` : 'Date changed';
    case 'MATCH_TIME_CHANGED':
      return details?.newValue ? `New time: ${details.newValue}` : 'Time changed';
    case 'MATCH_LOCATION_CHANGED':
      // Venue details rendered on separate line — no extra description needed
      return '';
    case 'MATCH_NOW_SCHEDULED': {
      // Message has complete info as "date, time @ venue" — strip venue
      const msg = notification.message || '';
      const atIdx = msg.indexOf(' @ ');
      return atIdx > 0 ? msg.slice(0, atIdx) : msg || 'Match has been scheduled';
    }
    case 'MATCH_NOW_PENDING':
      // New format has simple message, old format has teams embedded - use simple text
      return details?.newStatus ? 'New date/time TBD' : 'New date/time TBD';
    case 'CONFLICT_DETECTED':
      return 'Schedule conflict detected';
    case 'CONFLICT_RESOLVED': {
      // Show which matches are no longer overlapping
      if (details?.matches && Array.isArray(details.matches) && details.matches.length > 0) {
        const matchParts = details.matches.map((m: any) =>
          m.playerName ? `${m.playerName}: ${m.homeTeam} vs ${m.awayTeam}` : `${m.homeTeam} vs ${m.awayTeam}`
        );
        return `No longer overlapping: ${matchParts.join(', ')}`;
      }
      // Fallback to backend message which also has details
      return notification.message || 'Schedule conflict resolved';
    }
    case 'NEW_MATCH_ADDED': {
      // Strip venue from message (format: "date, time @ venue") — venue rendered separately
      const msg = notification.message || '';
      const atIdx = msg.indexOf(' @ ');
      return atIdx > 0 ? msg.slice(0, atIdx) : msg || 'New match added to schedule';
    }
    case 'SYNC_COMPLETE': {
      const d = notification.changeDetails;
      const parts: string[] = [];
      if (d?.matchesCreated) parts.push(`${d.matchesCreated} new`);
      if (d?.matchesUpdated) parts.push(`${d.matchesUpdated} updated`);
      if (d?.matchesDeleted) parts.push(`${d.matchesDeleted} removed`);
      return parts.length > 0 ? parts.join(', ') : 'No changes detected';
    }
    case 'SCORE_POSTED':
      return '';
    default:
      // Avoid showing messages that might contain team names
      return 'Schedule updated';
  }
};

/** Returns venue info and whether it was a move (changed) or just context (@ venue) */
const getVenueInfo = (notification: ScheduleNotification): { venue: string; moved: boolean } | null => {
  const details = notification.changeDetails;

  // Combined notification — new format: { changes: [...] }, old format: [...]
  const changesArray = Array.isArray(details) ? details : details?.changes;
  if (Array.isArray(changesArray)) {
    const loc = changesArray.find((d: any) => d.field === 'location');
    return loc?.newValue ? { venue: loc.newValue, moved: true } : null;
  }

  switch (notification.type) {
    case 'MATCH_LOCATION_CHANGED':
      return details?.newValue ? { venue: details.newValue, moved: true } : null;
    case 'MATCH_NOW_SCHEDULED': {
      if (details?.location) return { venue: details.location, moved: false };
      // Fallback: parse venue from message ("date, time @ venue")
      const atIdx = notification.message?.indexOf(' @ ');
      if (atIdx && atIdx > 0) {
        return { venue: notification.message!.slice(atIdx + 3), moved: false };
      }
      return null;
    }
    case 'NEW_MATCH_ADDED': {
      // Venue is embedded in message as "date, time @ venue"
      const atIdx = notification.message?.indexOf(' @ ');
      if (atIdx && atIdx > 0) {
        return { venue: notification.message!.slice(atIdx + 3), moved: false };
      }
      return null;
    }
    default:
      return null;
  }
};

interface NotificationBellProps {
  size?: 'small' | 'medium';
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ size = 'medium' }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Use the unified event stream for real-time updates
  const { unreadCount, resetUnreadCount } = useNotifications();

  const iconSize = size === 'small' ? 22 : 24;
  // Track dismiss triggers per notification (incrementing counter triggers animation)
  const [dismissTriggers, setDismissTriggers] = useState<Record<string, number>>({});
  // Pre-compute arrow position based on bell position and expected paper position
  const [arrowRight, setArrowRight] = useState(14);

  // Fetch recent notifications when menu opens
  // Data is pre-populated from SSE connection, so this is usually instant
  const { data: notificationsData, isLoading, isError } = useQuery(
    ['notifications', 'recent'],
    () => getNotifications(1, 5),
    {
      enabled: open,
      staleTime: 60000, // 1 minute - data is kept fresh via SSE
      retry: false, // Don't retry on failure - use cached data
      refetchOnMount: false, // Don't refetch if we have cached data
    }
  );

  // Only show loading spinner if we have no data AND not errored
  const showLoading = isLoading && !notificationsData && !isError;

  // Auto-mark all as read when dropdown opens
  useEffect(() => {
    if (open) {
      clearPushNotifications();
    }
    if (open && unreadCount > 0) {
      resetUnreadCount();
      if (navigator.onLine) {
        markAllAsRead().then(() => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });
      } else {
        enqueue({ type: 'markAllAsRead' });
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all notifications mutation
  const clearAllMutation = useMutation(
    () => {
      if (!navigator.onLine) {
        enqueue({ type: 'clearAll' });
        return Promise.resolve({ deletedCount: 0 });
      }
      return clearAllNotifications();
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['notifications', 'recent'] });
        const previous = queryClient.getQueryData(['notifications', 'recent']);
        queryClient.setQueryData(['notifications', 'recent'], (old: any) =>
          old ? { ...old, notifications: [], total: 0 } : old
        );
        resetUnreadCount();
        clearPushNotifications();
        handleClose();
        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          queryClient.setQueryData(['notifications', 'recent'], context.previous);
        }
      },
      onSettled: () => {
        if (navigator.onLine) {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      },
    }
  );

  // Delete single notification mutation
  const deleteNotificationMutation = useMutation(
    (notificationId: string) => {
      if (!navigator.onLine) {
        enqueue({ type: 'deleteNotification', payload: { id: notificationId } });
        return Promise.resolve();
      }
      return deleteNotification(notificationId);
    },
    {
      onMutate: async (notificationId: string) => {
        await queryClient.cancelQueries({ queryKey: ['notifications', 'recent'] });
        const previous = queryClient.getQueryData(['notifications', 'recent']);
        queryClient.setQueryData(['notifications', 'recent'], (old: any) =>
          old
            ? {
                ...old,
                notifications: old.notifications.filter(
                  (n: any) => n.id !== notificationId
                ),
                total: Math.max(0, (old.total || 0) - 1),
              }
            : old
        );
        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          queryClient.setQueryData(['notifications', 'recent'], context.previous);
        }
      },
      onSettled: () => {
        if (navigator.onLine) {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      },
    }
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const bell = event.currentTarget;
    const bellRect = bell.getBoundingClientRect();
    const bellCenterX = bellRect.left + bellRect.width / 2;
    const isMobilePortrait = window.innerWidth <= 600 && window.innerHeight > window.innerWidth;

    // On desktop, paper right-aligns to bell right edge
    // On portrait mobile, paper spans left:8 to right:8 → right edge is viewport - 8
    const paperRight = isMobilePortrait ? window.innerWidth - 8 : bellRect.right;
    setArrowRight(Math.max(8, paperRight - bellCenterX - 7));
    setAnchorEl(bell);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback(
    (notification: ScheduleNotification) => {
      handleClose();

      // SYNC_COMPLETE notifications navigate to leagues page
      if (notification.type === 'SYNC_COMPLETE') {
        navigate('/leagues');
        return;
      }

      // Navigate to schedule filtered to the exact match
      const params = new URLSearchParams();

      // Pass the exact match ID so the schedule page can filter to a single match
      const targetMatchId = notification.match?.id || notification.globalMatchId;
      if (targetMatchId) {
        params.set('matchId', targetMatchId);
      }

      // Set search text so the search bar shows context (and user can click X to clear)
      const details = notification.changeDetails as Record<string, any> | null;
      if (details?.homeTeam && details?.awayTeam) {
        params.set('search', `${details.homeTeam} vs ${details.awayTeam}`);
      } else if (notification.player?.name) {
        params.set('search', notification.player.name);
      } else if (notification.match?.homeTeam) {
        params.set('search', notification.match.homeTeam);
      }

      // Extract date from match if available (context for date range to load)
      if (notification.match?.dateTime) {
        const matchDate = new Date(notification.match.dateTime);
        params.set('date', matchDate.toISOString().split('T')[0]); // YYYY-MM-DD format
      }

      const queryString = params.toString();
      navigate(queryString ? `/schedule?${queryString}` : '/schedule');
    },
    [navigate, handleClose]
  );

  const handleViewAll = useCallback(() => {
    handleClose();
    navigate('/user-settings?tab=notifications');
  }, [navigate, handleClose]);

  const notifications = notificationsData?.notifications || [];

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-label={`${unreadCount} unread notifications`}
        sx={{
          color: open ? 'primary.main' : unreadCount > 0 ? 'text.primary' : 'action.active',
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16,
              padding: '0 4px',
              top: 2,
              right: 2,
            },
          }}
        >
          <NotificationsIcon sx={{ fontSize: iconSize }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 4,
          // Portrait mobile: full-width dropdown with margins
          '@media (max-width: 600px) and (orientation: portrait)': {
            '& .MuiPaper-root': {
              left: '8px !important',
              right: '8px !important',
              width: 'calc(100vw - 16px) !important',
              maxWidth: 'calc(100vw - 16px) !important',
            },
          },
          // Landscape mobile: full-screen shade below the app bar
          '@media (max-height: 500px) and (orientation: landscape)': {
            '& .MuiPaper-root': {
              position: 'fixed !important' as any,
              top: '48px !important',
              left: '0 !important',
              right: '0 !important',
              bottom: '0 !important',
              width: '100vw !important',
              maxWidth: '100vw !important',
              maxHeight: 'none !important',
              borderRadius: '0 !important',
            },
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: 360,
            maxHeight: 480,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 0.5,
            // Portrait mobile: just constrain width
            '@media (max-width: 600px) and (orientation: portrait)': {
              width: 'calc(100vw - 16px)',
              maxWidth: 'calc(100vw - 16px)',
            },
            // Landscape mobile: full shade
            '@media (max-height: 500px) and (orientation: landscape)': {
              filter: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
              mt: 0,
            },
            // Arrow (hidden in landscape shade mode)
            '&::before': {
              content: '""',
              display: 'block',
              '@media (max-height: 500px) and (orientation: landscape)': {
                display: 'none',
              },
              position: 'absolute',
              top: 0,
              right: arrowRight,
              width: 14,
              height: 14,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
            '& .MuiList-root': {
              maxHeight: 400,
              overflow: 'auto',
              pt: 0,
              '@media (max-height: 500px) and (orientation: landscape)': {
                maxHeight: 'none',
                flex: 1,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 2,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {notifications.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={() => clearAllMutation.mutate()}
              disabled={clearAllMutation.isLoading}
              sx={{ color: 'text.primary', borderColor: 'divider' }}
            >
              Clear all
            </Button>
          )}
        </Box>

        {/* Loading state - only if no cached data */}
        {showLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Empty state */}
        {!showLoading && notifications.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notifications yet</Typography>
          </Box>
        )}

        {/* Notification items */}
        {!showLoading &&
          notifications.map((notification, index) => {
            const Icon = NOTIFICATION_ICONS[notification.type] || NotificationsIcon;
            const iconColor = NOTIFICATION_COLORS[notification.type] || '#666';

            return (
              <Box key={notification.id}>
                {index > 0 && <Divider />}
                <SwipeableNotificationItem
                  onDismiss={() => deleteNotificationMutation.mutate(notification.id)}
                  disabled={deleteNotificationMutation.isLoading}
                  dismissTrigger={dismissTriggers[notification.id]}
                >
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    pr: 5,
                    alignItems: 'flex-start',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {/* Dismiss button */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDismissTriggers(prev => ({
                        ...prev,
                        [notification.id]: (prev[notification.id] || 0) + 1,
                      }));
                    }}
                    disabled={deleteNotificationMutation.isLoading}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 4,
                      p: 0.5,
                      color: 'text.disabled',
                      '&:hover': { color: 'text.secondary' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <Icon sx={{ color: iconColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={notification.read ? 400 : 600}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        {notification.changeDetails?.homeTeam && (
                          <Typography
                            variant="caption"
                            component="span"
                            color="text.primary"
                            sx={{
                              display: 'block',
                              lineHeight: 1.4,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {notification.changeDetails.homeTeam}
                            {' vs '}
                            {notification.changeDetails.awayTeam}
                          </Typography>
                        )}
                        {notification.changeDetails?.leagueName && (
                          <Typography
                            variant="caption"
                            component="span"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {notification.changeDetails.leagueName}
                            {notification.changeDetails.division ? ` · ${notification.changeDetails.division}` : ''}
                          </Typography>
                        )}
                        {getChangeDescription(notification) && (
                          <Typography
                            variant="caption"
                            component="span"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getChangeDescription(notification)}
                          </Typography>
                        )}
                        {(() => {
                          const venueInfo = getVenueInfo(notification);
                          if (!venueInfo) return null;
                          return (
                            <Typography
                              variant="caption"
                              component="span"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {venueInfo.moved ? 'Moved to' : '@'} {venueInfo.venue}
                            </Typography>
                          );
                        })()}
                        <Typography variant="caption" component="span" color="text.disabled" sx={{ display: 'block' }}>
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </Typography>
                      </>
                    }
                  />
                </MenuItem>
                </SwipeableNotificationItem>
              </Box>
            );
          })}

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center', flexShrink: 0 }}>
              <Button variant="outlined" size="small" onClick={handleViewAll} startIcon={<SettingsIcon />} sx={{ color: 'text.primary', borderColor: 'divider' }}>
                Settings & History
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
